import { TFunction } from 'i18next'

import { Primitive } from '@global/types/Primitive'

import { isEmpty } from './isEmpty'
import { getRegexErrorMessage } from './validations'

export const EmailPattern =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{1,}))$/

export const BEEmailPattern = '^([a-zA-Z0-9_\\-\\.\\+]+)@([a-zA-Z0-9_\\-\\.]+)\\.([a-zA-Z]{2,10})$'

export const UpperCasePattern = /[A-Z]/
export const LowerCasePattern = /[a-z]/

export const NumberPattern = /^[0-9]+$/g
export const NonDigitPattern = /\D/g

export const FullNameLettersPattern = /^((?:Test_)*(?:TEST_)*(?:test_)*[A-Za-z-\s.@/]+)$/
export const EnglishLettersPattern = /^[A-Za-z0-9-\s./*@!#%&()^~{}+%$-]+$/
export const AddressLettersPattern = /^[A-Za-z0-9\s-.,&#/]*$/
export const CityStatePattern = /^[A-Za-z0-9\s]+$/
export const ZipCodePattern = /^[A-Za-z0-9\s-]+$/
export const RestrictedSpecialCharacters = /[㋡㋛☻〠¤¶¬¦±°•…†‡‰Œ™¨©®¹²³¼½¾ºªˉ]/g

export const EmojiPattern =
  /(?:[\u2700-\u27bf]|(?:\ud83c[\udde6-\uddff]){2}|[\ud800-\udbff][\udc00-\udfff]|[\u0023-\u0039]\ufe0f?\u20e3|\u3299|\u3297|\u303d|\u3030|\u24c2|\ud83c[\udd70-\udd71]|\ud83c[\udd7e-\udd7f]|\ud83c\udd8e|\ud83c[\udd91-\udd9a]|\ud83c[\udde6-\uddff]|\ud83c[\ude01-\ude02]|\ud83c\ude1a|\ud83c\ude2f|\ud83c[\ude32-\ude3a]|\ud83c[\ude50-\ude51]|\u203c|\u2049|[\u25aa-\u25ab]|\u25b6|\u25c0|[\u25fb-\u25fe]|\u00a9|\u00ae|\u2122|\u2139|\ud83c\udc04|[\u2600-\u26FF]|\u2b05|\u2b06|\u2b07|\u2b1b|\u2b1c|\u2b50|\u2b55|\u231a|\u231b|\u2328|\u23cf|[\u23e9-\u23f3]|[\u23f8-\u23fa]|\ud83c\udccf|\u2934|\u2935|[\u2190-\u21ff])/g
export const UrlPattern =
  /^(?:(ftp|http|https):\/\/)?(www\.)?[a-zA-Z0-9-]+\.[a-zA-Z]{2,}(\/[^\s]*)?$/
export const UrlHttpPattern = /^(f|ht)tps?:\/\//i
export const IBPattern = /^IB.*$/i

export type FormSubmitValues<T> = {
  [K in keyof T]: T[K] | null
}

export const validateRequired =
  (trans: string) =>
  (value: Primitive): string | undefined => {
    if (isEmpty(value)) {
      return trans
    }
    return undefined
  }

export const validateField =
  (t: TFunction, required: boolean, validationRegex: string | null) =>
  (value: string): string | undefined => {
    if (required && isEmpty(value)) {
      return t('Validation.Required')
    }

    if (!isEmpty(value) && validationRegex && !new RegExp(validationRegex).test(value)) {
      if (validationRegex === BEEmailPattern) {
        return t('Validation.Please enter a valid email address to proceed')
      } else {
        return getRegexErrorMessage(t, validationRegex)
      }
    }
  }

export const validateIsTrue =
  (trans: string) =>
  (value: Primitive): string | undefined => {
    if (isEmpty(value)) {
      return trans
    }

    if (value === false) {
      return trans
    }

    return undefined
  }

export const validateIsNumberPositive =
  (trans: string) =>
  (value: number): string | undefined => {
    if (isEmpty(value)) {
      return trans
    }

    if (value <= 0) {
      return trans
    }

    return undefined
  }

export const extractDigitsFromString = (value: string): string => {
  return value.replace(NonDigitPattern, '')
}

export const createInputPhoneNumberFilter =
  (pattern = /\D/g, replacement = '') =>
  (event: React.FormEvent<HTMLInputElement>) => {
    const input = event.currentTarget
    input.value = input.value.replace(pattern, replacement)
  }
